import type { App, Environment } from '@hangar/shared';
import type { BuildsSearch } from '@/router';
import { cn } from '@/lib/utils';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AppSelect, BranchInput, EnvironmentSelect, PlatformSelect, StatusFilter } from './BuildsFilterControls';
import { BuildsFiltersSheet } from './BuildsFiltersSheet';
import { PRESETS, isFiltered, type FilterPreset } from './buildsFiltersModel';
import { useCommittedText } from './useCommittedText';

export { statusesOf } from './buildsFiltersModel';

interface Props {
  search: BuildsSearch;
  onChange: (next: BuildsSearch) => void;
  apps: App[] | undefined;
  environments: Environment[];
  total?: number;
}

/**
 * Builds filter bar. Tablet and desktop: quick filters + search, then one row of controls. Phone: quick filters, then
 * the search box and one "Filters" button that opens the same controls in a bottom sheet (one variant mounted at a time,
 * so no test id is duplicated).
 */
export function BuildsFilters({ search, onChange, apps, environments, total }: Props) {
  const [q, setQ] = useCommittedText('q', search, onChange);
  const [branch, setBranch] = useCommittedText('branch', search, onChange);
  const phone = useBreakpoint() === 'phone';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <QuickFilters search={search} onChange={onChange} total={total} />
        {phone ? null : <SearchBox value={q} onChange={setQ} />}
      </div>
      {phone ? (
        <div className="flex items-center gap-2">
          <PhoneSearch value={q} onChange={setQ} />
          <BuildsFiltersSheet search={search} onChange={onChange} apps={apps} environments={environments} branch={branch} onBranchChange={setBranch} />
        </div>
      ) : (
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:thin]">
          <span className="shrink-0 text-[13px] text-subtle">Filter by</span>
          <AppSelect search={search} onChange={onChange} apps={apps} />
          <EnvironmentSelect search={search} onChange={onChange} environments={environments} />
          <PlatformSelect search={search} onChange={onChange} />
          <StatusFilter search={search} onChange={onChange} />
          <BranchInput value={branch} onChange={setBranch} />
          {isFiltered(search) ? <ResetButton onReset={() => onChange({})} /> : null}
        </div>
      )}
    </div>
  );
}

/** One row, whole (malible-premium-ui §2.7): chips size to their text and scroll on narrow phones. */
function QuickFilters({ search, onChange, total }: { search: BuildsSearch; onChange: (next: BuildsSearch) => void; total?: number }) {
  return (
    <div role="tablist" aria-label="Quick filters" className="flex min-w-0 flex-1 gap-1 overflow-x-auto rounded-lg border bg-secondary p-1">
      {PRESETS.map((p) => (
        <PresetTab key={p.key} preset={p} active={p.match(search)} total={total} onSelect={() => onChange(p.apply(search))} />
      ))}
    </div>
  );
}

function PresetTab({ preset, active, total, onSelect }: { preset: FilterPreset; active: boolean; total?: number; onSelect: () => void }) {
  const showTotal = preset.key === 'all' && total !== undefined;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={preset.testId}
      onClick={onSelect}
      className={cn(
        'h-8 shrink-0 rounded-md px-3 text-[13px] font-medium transition-colors duration-150 max-sm:h-11',
        active ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {preset.label}
      {showTotal ? <span className="tabular ml-1.5 font-mono text-[11px] text-subtle">{total}</span> : null}
    </button>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative w-64">
      <Icon name="search" size={18} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
      <Input
        data-testid="builds-search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="my-app#12"
        aria-label="Search by reference"
        className="pl-9 font-mono sm:text-[13px]"
        spellCheck={false}
      />
    </div>
  );
}

function PhoneSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Input
      data-testid="builds-search-phone"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="my-app#12"
      aria-label="Search by reference"
      className="h-11 min-w-0 flex-1 font-mono text-[13px]"
      spellCheck={false}
    />
  );
}

function ResetButton({ onReset }: { onReset: () => void }) {
  return (
    <Button variant="link" size="sm" data-testid="builds-filter-reset" className="shrink-0 px-1 text-subtle" onClick={onReset}>
      Reset
    </Button>
  );
}
